import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvolvements } from '../../hooks/useinvolvements';
import { involvementsService } from '../../services/dataService';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Edit2, Trash2 } from 'lucide-react';

const AdminInvolvements = () => {
  const navigate = useNavigate();
  const { involvements, loading, error, refetch } = useInvolvements();
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id, title) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      setDeleting(id);
      try {
        const result = await involvementsService.delete(id);
        if (result.success) {
          toast.success('Involvement deleted successfully');
          refetch();
        } else {
          toast.error('Failed to delete involvement');
        }
      } catch (error) {
        toast.error('Error deleting involvement');
      } finally {
        setDeleting(null);
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={() => navigate('/adminaccess/involvements/new')}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Involvement</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Manage Involvements</h1>
          <p className="text-white/60">Leadership positions and organizational roles</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading involvements...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}

        {/* Involvements List */}
        {!loading && !error && (
          <div className="space-y-4">
            {involvements.length === 0 ? (
              <div className="p-12 rounded-lg border border-white/10 bg-white/5 text-center">
                <p className="text-white/40 mb-4">No involvements yet</p>
                <button
                  onClick={() => navigate('/adminaccess/involvements/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Your First Involvement</span>
                </button>
              </div>
            ) : (
              involvements.map((involvement) => (
                <div
                  key={involvement.id}
                  className="p-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex gap-4">
                    {/* Logo */}
                    {involvement.image && (
                      <img
                        src={involvement.image}
                        alt={involvement.organization}
                        className="w-20 h-20 rounded-lg object-cover border border-white/10"
                      />
                    )}

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-medium mb-1">{involvement.role}</h3>
                          <div className="text-white/70 mb-1">{involvement.organization}</div>
                          {involvement.institution && (
                            <div className="text-white/50 text-sm">{involvement.institution}</div>
                          )}
                          <div className="text-white/40 text-sm mt-1">{involvement.period}</div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/adminaccess/involvements/edit/${involvement.id}`)}
                            className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(involvement.id, involvement.role)}
                            disabled={deleting === involvement.id}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {involvement.description && (
                        <p className="text-sm text-white/60 mt-2 line-clamp-2">
                          {involvement.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminInvolvements;